/***
 * Created on 2023/8/23
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/

import React, {useRef, useState} from 'react';
import ModalDialog from "../../../../common/modal_dialog/modalDialog";
import {useTranslation} from "react-i18next";
import {useCaptcha} from "../../../../../hook/captcha";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {apiGet, apiPost} from "../../../../../service/api";
import {useOnChange} from "../../../../../hook/form";


function SetupAuthApp({dispatch, ...prop}) {
    const { t } = useTranslation();
    const {onChange, values, setValues} = useOnChange({code: ''});
    const {Captcha, hasCaptcha, captchaValue, captchaError} = useCaptcha(onChange);
    const queryClient = useQueryClient();
    const modalRef = useRef();
    const [success, setSuccess] = useState(false);

    const updateTwoStepMutation = useMutation({
        mutationFn: async (data) => await apiPost(`/ajax/two_step`, data),
        onSuccess: data => {
            queryClient.invalidateQueries({
                queryKey: ['profile']
            })
            setSuccess(true);
        },
        onError: (error, variables, context) => {
            // const message = error?.cause ? error.cause?.message : error.toString();
            // modalRef.current.alert('error', true, message);
            console.error(error);
        }
    });

    const initAuthApp = useQuery({
        queryKey: ['init_authenticator_secret'],
        queryFn: async () => await apiGet(`/ajax/init_google_secret_key`)
    });

    const onVerify = async (e) => {
        const data = {
            ...values,
            verify_type: 'google_auth'
        };
        await updateTwoStepMutation.mutateAsync(data);
    }

    return (
        <ModalDialog {...prop} title={t('@Verify Code@')} cancelText={t('@Close@')} hideSubmit={true} ref={modalRef}>
            <div className="dialog-content dialog-content--verify-code">
                {
                    !success &&
                    <>

                        <div className="verify-code__step-title">
                            <div className="verify-code__step-title__step"><span>1</span></div>
                            <div>{t('@Scan the following QR code with the authentication app on your mobile device.@')}</div>
                        </div>
                        <div className="dialog__info-grid dialog__info-grid--otp !mb-24">
                            <div className="dialog__info-grid--otp__qrcode"><img src={initAuthApp.isFetching ? '' : initAuthApp?.data?.qrcode} alt=""/></div>
                            <div className="dialog__info-grid--otp__code">
                                <p>{t('@Or enter the key manually:@')}</p>
                                <p className="text-22 letter-sp1 color-primary">{initAuthApp?.data?.secret}</p>
                            </div>
                        </div>
                        <div className="verify-code__step-title">
                            <div className="verify-code__step-title__step"><span>2</span></div>
                            <div>{t('@Enter the six digit code provided by the app.@')}</div>
                        </div>
                        <div className="dialog__form-group">
                            <input type="text" name="code"
                                className="w-full max-w-full input--must-fill"
                                maxLength={6}
                                pattern="\d*"
                                value={values.code}
                                onChange={onChange} />
                        </div>

                        <button className="q-btn q-btn--default btn-rounded-full !mb-16" disabled={values.code?.length !== 6} onClick={onVerify}>{t('@Verify@')}</button>

                        {hasCaptcha && Captcha}

                        <br/>
                        <p dangerouslySetInnerHTML={{__html: t('@If you have not yet install any authentication app, you may need to download {google_authenticator} or {microsoft_authenticator} for your mobile device.@', {
                            google_authenticator: '<a href="https://support.google.com/accounts/answer/1066447" target="_blank">Google Authenticator</a>',
                            microsoft_authenticator: '<a href="https://go.microsoft.com/fwlink/?linkid=866594" target="_blank">Microsoft Authenticator</a>',
                            interpolation: {
                                prefix: '{',
                                suffix: '}',
                                escapeValue: false
                            }
                        })}}/>
                    </>
                }

                {
                    success && <>
                        <div className="dialog__sub-title color-primary">{t('@Code accepted!@')}</div>
                        <p>{t('@You will use Authenticator app to sign in your QNAP Account now.@')}</p>
                    </>
                }

            </div>
        </ModalDialog>
    );
}

export default SetupAuthApp;